// ===========================================
// HOOKS: Calendario (CORE)
// ===========================================

import { useCallback, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { calendarioService, EventoEliminadoError } from '../services/calendarioService'
import { calcularEstadisticasVencimientos } from '@/lib/vencimientos-utils'
import { useNotificacionesModulo } from './useNotificacionesModulo'
import { useAuditor } from '@/hooks/useAuditor'
import { usePermissions } from '@/hooks/usePermissions'
import { aplicarCategoriaPresentada } from '@/hooks/aplicarCategoriaPresentada'
import { notificarCambiosCategoria } from '@/hooks/notificarCambioCategoria'
import { clientesKeys } from '@/hooks/useClientes'
import { assertPermiso, handleMutationError } from '@/lib/permisos-guard'
import { useSupabase } from '@/lib/supabase-context'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { toast } from 'sonner'
import type { FilaEstadistica, ScopingResponsable } from '@/lib/vencimientos-utils'
import type { EstadisticasVencimientos } from '@/types/vencimientos'
import type { MarcarPresentadoData } from '../components/calendario/MarcarPresentadoDialog'
import type {
  RegistrarPagoData,
  RegistrarVEPData,
  MarcarPlanPagoData,
  DatosMaterializacion,
  ProrrogaInsert,
  CrearAlertaData,
} from '@/types/vencimientos'
import type {
  FiltrosVencimientos,
  FiltrosEventos,
  FiltrosCalendario,
  EventoCalendario,
  EventoCalendarioInsert,
  EventoCalendarioUpdate,
  EventoOcupado,
} from '../types/calendario'

// ===========================================
// Query Keys
// ===========================================

export const calendarioKeys = {
  all: ['calendario'] as const,
  // Vencimientos (scoped por organización)
  proximos: (orgId: string | null, dias?: number) => [...calendarioKeys.all, 'proximos', orgId, dias] as const,
  mesvencimientos: (orgId: string | null, year: number, month: number) =>
    [...calendarioKeys.all, 'mes', orgId, year, month] as const,
  porDia: (orgId: string | null, year: number, month: number) =>
    [...calendarioKeys.all, 'por-dia', orgId, year, month] as const,
  listaVencimientos: (orgId: string | null, filtros?: FiltrosVencimientos) =>
    [...calendarioKeys.all, 'lista', orgId, filtros] as const,
  vencidos: (orgId: string | null) => [...calendarioKeys.all, 'vencidos', orgId] as const,
  descartados: (orgId: string | null) => [...calendarioKeys.all, 'descartados', orgId] as const,
  cerrados: (orgId: string | null, desde?: string, hasta?: string) =>
    [...calendarioKeys.all, 'cerrados', orgId, desde, hasta] as const,
  alertas: (orgId: string | null, usuarioId: string | null) =>
    [...calendarioKeys.all, 'alertas', orgId, usuarioId] as const,
  porCliente: (orgId: string | null, clienteId: string) =>
    [...calendarioKeys.all, 'cliente', orgId, clienteId] as const,
  estadisticas: (orgId: string | null) => [...calendarioKeys.all, 'estadisticas', orgId] as const,
  tiposImpuesto: () => [...calendarioKeys.all, 'tipos-impuesto'] as const,
  // Eventos
  eventos: (filtros?: FiltrosEventos) => [...calendarioKeys.all, 'eventos', filtros] as const,
  eventosMes: (orgId: string | null, year: number, month: number, workspace?: string) =>
    [...calendarioKeys.all, 'eventos-mes', orgId, year, month, workspace] as const,
  evento: (id: string) => [...calendarioKeys.all, 'evento', id] as const,
  invitadosReunion: (eventoId: string) => [...calendarioKeys.all, 'invitados-reunion', eventoId] as const,
  // Unificado (scoped por organización)
  items: (orgId: string | null, year: number, month: number, filtros?: FiltrosCalendario) =>
    [...calendarioKeys.all, 'items', orgId, year, month, filtros] as const,
  itemsPorDia: (orgId: string | null, year: number, month: number, filtros?: FiltrosCalendario) =>
    [...calendarioKeys.all, 'items-por-dia', orgId, year, month, filtros] as const,
  // Feriados
  feriados: (anio: number) => [...calendarioKeys.all, 'feriados', anio] as const,
}

// ===========================================
// Hooks de Vencimientos
// ===========================================

/**
 * Hook para obtener próximos vencimientos
 */
export function useProximosVencimientos(dias: number = 30) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: calendarioKeys.proximos(organizacionId, dias),
    // ORR-73: incluirCompletados=true para que el filtro client-side de
    // "presentados" funcione. Sin esto, los presentados nunca se cargaban
    // y el filtro siempre devolvía vacío.
    queryFn: () => calendarioService.obtenerProximosVencimientos(supabase!, organizacionId!, dias, true),
    enabled: !!supabase && !supabaseLoading && !!organizacionId,
    refetchInterval: 5 * 60 * 1000,
  })
}

/**
 * Hook para obtener vencimientos de un mes
 */
export function useVencimientosMes(year: number, month: number) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: calendarioKeys.mesvencimientos(organizacionId, year, month),
    queryFn: () => calendarioService.obtenerVencimientosPorMes(supabase!, organizacionId!, year, month),
    enabled: !!supabase && !supabaseLoading && !!organizacionId,
  })
}

/**
 * Hook para obtener vencimientos agrupados por día
 */
export function useVencimientosPorDia(year: number, month: number) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: calendarioKeys.porDia(organizacionId, year, month),
    queryFn: () => calendarioService.obtenerVencimientosPorDia(supabase!, organizacionId!, year, month),
    enabled: !!supabase && !supabaseLoading && !!organizacionId,
    placeholderData: (previousData) => previousData,
  })
}

/**
 * Hook para listar vencimientos con filtros
 */
export function useVencimientos(filtros?: FiltrosVencimientos) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: calendarioKeys.listaVencimientos(organizacionId, filtros),
    queryFn: () => calendarioService.listarVencimientos(supabase!, organizacionId!, filtros),
    enabled: !!supabase && !supabaseLoading && !!organizacionId,
  })
}

/**
 * Hook para obtener vencimientos vencidos
 */
export function useVencimientosVencidos() {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: calendarioKeys.vencidos(organizacionId),
    queryFn: () => calendarioService.obtenerVencimientosVencidos(supabase!, organizacionId!),
    enabled: !!supabase && !supabaseLoading && !!organizacionId,
  })
}

/**
 * Hook para obtener vencimientos de un cliente
 */
export function useVencimientosCliente(clienteId: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: calendarioKeys.porCliente(organizacionId, clienteId),
    queryFn: () => calendarioService.obtenerVencimientosPorCliente(supabase!, organizacionId!, clienteId),
    enabled: !!supabase && !supabaseLoading && !!clienteId && !!organizacionId,
  })
}

/**
 * Hook para obtener estadísticas de vencimientos
 */
export function useEstadisticasVencimientos(scoping?: ScopingResponsable) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()

  // El conteo va en `select` y NO en la queryFn, con la key sin el scoping (ORR-382). Contarlo del
  // lado del servidor obligaba a meter el scoping en la key, y el `reparto` no puede viajar ahí:
  // cuando alguien cambia a quién le toca un concepto, la key no cambiaba y las tarjetas seguían
  // mostrando los dueños viejos hasta el refetch siguiente. Acá se recalcula apenas cambia el
  // scoping, sin depender de ninguna invalidación, y sin volver a pedir las filas.
  const calcular = useCallback(
    (filas: FilaEstadistica[]) => calcularEstadisticasVencimientos(filas, scoping),
    [scoping],
  )

  // Los genéricos van explícitos: con `select` inferido, TS da la unión de los dos tipos y el
  // consumidor recibe `FilaEstadistica[] | EstadisticasVencimientos`.
  return useQuery<FilaEstadistica[], Error, EstadisticasVencimientos>({
    queryKey: calendarioKeys.estadisticas(organizacionId),
    queryFn: () => calendarioService.obtenerFilasEstadisticas(supabase!, organizacionId!),
    select: calcular,
    refetchInterval: 5 * 60 * 1000,
    enabled: !!supabase && !supabaseLoading && !!organizacionId,
  })
}

/**
 * Hook para obtener tipos de impuesto disponibles
 */
export function useTiposImpuesto() {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: calendarioKeys.tiposImpuesto(),
    queryFn: () => calendarioService.obtenerTiposImpuesto(supabase!, ),
      enabled: !!supabase && !supabaseLoading,
  })
}

/**
 * Hook para marcar vencimiento como presentado (con materialización lazy)
 */
export function useMarcarPresentado() {
  const { user } = useAuth0()
  const queryClient = useQueryClient()
  const { cambiarEstado } = useAuditor()
  const { supabase } = useSupabase()
  const { puede } = usePermissions()
  const { organizacionId } = useOrganizacion()
  const { notificar, responsablesCliente, nombreCliente } = useNotificacionesModulo()

  return useMutation({
    mutationFn: ({ id, datos, materializacion }: {
      id: string
      datos: MarcarPresentadoData
      materializacion?: DatosMaterializacion
    }) => {
      assertPermiso(puede, 'editarVencimiento')
      return calendarioService.marcarVencimientoPresentado(supabase!, id, datos, materializacion)
    },
    onSuccess: async (data, variables) => {
      queryClient.invalidateQueries({ queryKey: calendarioKeys.all })
      // A virtual vencimiento is stored by the RPC, and the history goes to the stored row it returns.
      cambiarEstado('vencimiento', data?.id ?? variables.id, 'pendiente', 'presentado')

      // ORR-323: la categoría elegida al presentar la recategorización va a la faceta del
      // cliente. Acá y no en mutationFn: la RPC ya corrió y no se revierte, así que un fallo
      // de este efecto no puede convertir una presentación hecha en "Error al marcar como
      // presentado". El fallo se avisa igual, con toast propio. `onSuccess` se espera antes
      // de que resuelva `mutateAsync`, así que la faceta queda escrita antes del mail.
      const categoria = variables.datos.categoria_monotributo
      if (categoria && data?.cliente_id && supabase && organizacionId) {
        const resultado = await aplicarCategoriaPresentada(supabase, {
          clienteId: data.cliente_id,
          organizacionId,
          categoria,
        })
        queryClient.invalidateQueries({ queryKey: clientesKeys.all })
        if (!resultado.ok) {
          toast.error('Presentado, pero la categoría del cliente no se actualizó. Cambiala desde la ficha.')
        } else if (resultado.cambio) {
          // Aviso a responsables (ORR-351), best-effort como el de abajo.
          try {
            const nombre = await nombreCliente(data.cliente_id)
            await notificarCambiosCategoria(supabase, user?.email, [{ ...resultado.cambio, nombre }], user?.name)
          } catch (err) {
            console.error('[notificaciones] error avisando el cambio de categoría:', err)
          }
        }
      }

      // Notif: vencimiento presentado → notificar a responsables del cliente.
      // Migrado del patrón legacy de auto-notif al canónico (M1 cleanup post-review).
      if (!data?.cliente_id) return
      try {
        const clienteDisplay = await nombreCliente(data.cliente_id)
        const actor = user?.name || user?.email || 'Alguien'
        const destinatarios = await responsablesCliente(data.cliente_id)
        if (destinatarios.length === 0) return
        await notificar({
          tipo: 'vencimiento',
          titulo: 'Vencimiento presentado',
          mensaje: `${actor} marcó como presentado ${data.impuesto_nombre ?? 'un vencimiento'} (${data.periodo ?? '—'}) de ${clienteDisplay}`,
          enlace: `/calendario?vencimiento=${data.id}`,
          destinatarios,
        })
      } catch (err) {
        console.error('[notificaciones] error notificando vencimiento presentado:', err)
      }
    },
  })
}

/**
 * Hook para crear vencimiento
 */
export function useCrearVencimiento() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { crear } = useAuditor()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: (vencimiento: {
      cliente_id: string
      impuesto_nombre: string
      periodo: string
      fecha_vencimiento: string
      observaciones?: string
      created_by?: string
    }) => {
      assertPermiso(puede, 'crearVencimiento')
      return calendarioService.crearVencimiento(supabase!, vencimiento)
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: calendarioKeys.all })
      crear('vencimiento', data.id, {
        impuesto_nombre: data.impuesto_nombre,
        periodo: data.periodo,
      })
    },
  })
}

/**
 * Hook para actualizar vencimiento
 */
export function useActualizarVencimiento() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { editar } = useAuditor()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: ({ id, datos }: { id: string; datos: Record<string, unknown> }) => {
      assertPermiso(puede, 'editarVencimiento')
      return calendarioService.actualizarVencimiento(supabase!, id, datos)
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: calendarioKeys.all })
      editar('vencimiento', variables.id, { cambios: variables.datos })
    },
  })
}

/**
 * Hook para eliminar vencimiento
 */
export function useEliminarVencimiento() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { eliminar } = useAuditor()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: (id: string) => {
      assertPermiso(puede, 'editarVencimiento')
      return calendarioService.eliminarVencimiento(supabase!, id)
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: calendarioKeys.all })
      eliminar('vencimiento', id, {})
    },
  })
}

/**
 * Hook para obtener los vencimientos descartados (papelera). Fuente del filtro "Descartado".
 */
export function useVencimientosDescartados(enabled = true) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: calendarioKeys.descartados(organizacionId),
    queryFn: () => calendarioService.obtenerVencimientosDescartados(supabase!, organizacionId!),
    enabled: enabled && !!supabase && !supabaseLoading && !!organizacionId,
  })
}

/**
 * Presentados, pagados y en plan de pago cuyo día de cierre cae en el rango de la Lista.
 * Corre solo con los dos bordes del rango.
 */
export function useVencimientosCerrados(desde?: string, hasta?: string, enabled = true) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: calendarioKeys.cerrados(organizacionId, desde, hasta),
    queryFn: () =>
      calendarioService.obtenerCerradosPorFechaCierre(supabase!, organizacionId!, desde!, hasta!),
    enabled: enabled && !!desde && !!hasta && !!supabase && !supabaseLoading && !!organizacionId,
    refetchInterval: 5 * 60 * 1000,
  })
}

/**
 * Hook para descartar un vencimiento virtual (ORR-178: exclusión por-obligación)
 */
export function useDescartarVencimiento() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: ({ calendarioAfipId, clienteId }: { calendarioAfipId: string; clienteId: string }) => {
      assertPermiso(puede, 'descartarVencimiento')
      return calendarioService.descartarVencimiento(supabase!, calendarioAfipId, clienteId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calendarioKeys.all })
    },
  })
}

/**
 * ORR-234: hook para descartar un vencimiento físico sin calendario AFIP (recurrente / manual).
 * Mismo permiso que el descarte de virtuales: es la misma acción sobre otra fuente.
 */
export function useDescartarVencimientoFisico() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: (id: string) => {
      assertPermiso(puede, 'descartarVencimiento')
      return calendarioService.descartarVencimientoFisico(supabase!, id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calendarioKeys.all })
    },
  })
}

/**
 * ORR-232: alertas programadas por el usuario actual.
 *
 * Sirve para dos cosas: marcar en la planilla los vencimientos que ya tienen alerta y
 * permitir quitarla. Son pocas filas por usuario, así que se traen todas de una vez en
 * lugar de consultar por fila.
 */
export function useAlertasVencimiento(enabled = true) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId, usuario } = useOrganizacion()

  return useQuery({
    queryKey: calendarioKeys.alertas(organizacionId, usuario?.id ?? null),
    queryFn: () => calendarioService.obtenerAlertas(supabase!, organizacionId!, usuario!.id),
    enabled: enabled && !!supabase && !supabaseLoading && !!organizacionId && !!usuario?.id,
  })
}

/**
 * ORR-232: programar una alerta. Sin assertPermiso: la alerta es personal —el usuario se
 * la programa a sí mismo y no modifica datos de negocio—, así que no hay flag de RBAC que
 * la gobierne.
 */
export function useCrearAlertaVencimiento() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { organizacionId, usuario } = useOrganizacion()

  return useMutation({
    mutationFn: (datos: CrearAlertaData) =>
      calendarioService.crearAlerta(supabase!, organizacionId!, usuario!.id, datos),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calendarioKeys.all })
    },
  })
}

/**
 * ORR-232: quitar una alerta programada.
 */
export function useEliminarAlertaVencimiento() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()

  return useMutation({
    mutationFn: (id: string) => calendarioService.eliminarAlerta(supabase!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calendarioKeys.all })
    },
  })
}

/**
 * Hook para restaurar un vencimiento descartado (exclusión nueva o vf.descartado legacy)
 */
export function useRestaurarVencimiento() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: (id: string) => {
      assertPermiso(puede, 'restaurarVencimiento')
      return calendarioService.restaurarVencimiento(supabase!, id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calendarioKeys.all })
    },
  })
}

/**
 * Hook para revertir una presentación marcada por error (presentado → pendiente).
 * RPC con guardas (bloquea si ya tiene pago/VEP/plan). Notifica a responsables del cliente.
 */
export function useRevertirPresentado() {
  const { user } = useAuth0()
  const queryClient = useQueryClient()
  const { cambiarEstado } = useAuditor()
  const { supabase } = useSupabase()
  const { puede } = usePermissions()
  const { notificar, responsablesCliente, nombreCliente } = useNotificacionesModulo()

  return useMutation({
    mutationFn: (id: string) => {
      assertPermiso(puede, 'editarVencimiento')
      return calendarioService.revertirVencimientoPresentado(supabase!, id)
    },
    onSuccess: async (data, id) => {
      queryClient.invalidateQueries({ queryKey: calendarioKeys.all })
      cambiarEstado('vencimiento', id, 'presentado', 'pendiente')

      // Notif: presentación revertida → notificar a responsables del cliente (actor ≠ destinatario).
      if (!data?.cliente_id) return
      try {
        const clienteDisplay = await nombreCliente(data.cliente_id)
        const actor = user?.name || user?.email || 'Alguien'
        const destinatarios = await responsablesCliente(data.cliente_id)
        if (destinatarios.length === 0) return
        await notificar({
          tipo: 'vencimiento',
          titulo: 'Presentación revertida',
          mensaje: `${actor} revirtió la presentación de ${data.impuesto_nombre ?? 'un vencimiento'} (${data.periodo ?? '—'}) de ${clienteDisplay}`,
          enlace: `/calendario?vencimiento=${data.id}`,
          destinatarios,
        })
      } catch (err) {
        console.error('[notificaciones] error notificando reversión:', err)
      }
    },
  })
}

/**
 * Hook para actualizar estado de vencimientos vencidos
 */
export function useActualizarVencidos() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()

  return useMutation({
    mutationFn: () => calendarioService.actualizarEstadoVencidos(supabase!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calendarioKeys.all })
    },
  })
}

// ===========================================
// Hooks de Ciclo de Pago
// ===========================================

/**
 * Hook para registrar pago de un vencimiento (con materialización lazy)
 */
export function useRegistrarPago() {
  const { user } = useAuth0()
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { cambiarEstado } = useAuditor()
  const { puede } = usePermissions()
  const { notificar, responsablesCliente, nombreCliente } = useNotificacionesModulo()

  return useMutation({
    mutationFn: ({ id, datos, materializacion }: {
      id: string
      datos: RegistrarPagoData
      materializacion?: DatosMaterializacion
    }) => {
      assertPermiso(puede, 'editarVencimiento')
      return calendarioService.registrarPago(supabase!, id, datos, materializacion)
    },
    onSuccess: async (data, variables) => {
      queryClient.invalidateQueries({ queryKey: calendarioKeys.all })
      cambiarEstado('vencimiento', variables.id, 'presentado', 'pagado')

      // Notif: pago de vencimiento registrado → responsables del cliente.
      // Migrado del patrón legacy de auto-notif al canónico (M1 cleanup post-review).
      if (!data?.cliente_id) return
      try {
        const clienteDisplay = await nombreCliente(data.cliente_id)
        const actor = user?.name || user?.email || 'Alguien'
        const destinatarios = await responsablesCliente(data.cliente_id)
        if (destinatarios.length === 0) return
        await notificar({
          tipo: 'vencimiento',
          titulo: 'Pago de vencimiento registrado',
          mensaje: `${actor} registró el pago de ${data.impuesto_nombre ?? 'un vencimiento'} (${data.periodo ?? '—'}) de ${clienteDisplay}`,
          enlace: `/calendario?vencimiento=${data.id}`,
          destinatarios,
        })
      } catch (err) {
        console.error('[notificaciones] error notificando pago de vencimiento:', err)
      }
    },
  })
}

/**
 * Hook para registrar VEP en un vencimiento (con materialización lazy)
 */
export function useRegistrarVEP() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: ({ id, datos, materializacion }: {
      id: string
      datos: RegistrarVEPData
      materializacion?: DatosMaterializacion
    }) => {
      assertPermiso(puede, 'editarVencimiento')
      return calendarioService.registrarVEP(supabase!, id, datos, materializacion)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calendarioKeys.all })
    },
  })
}

/**
 * Hook para marcar vencimiento en plan de pago (con materialización lazy)
 */
export function useMarcarPlanPago() {
  const { user } = useAuth0()
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { cambiarEstado } = useAuditor()
  const { puede } = usePermissions()
  const { notificar, responsablesCliente, nombreCliente } = useNotificacionesModulo()

  return useMutation({
    mutationFn: ({ id, datos, materializacion }: {
      id: string
      datos: MarcarPlanPagoData
      materializacion?: DatosMaterializacion
    }) => {
      assertPermiso(puede, 'editarVencimiento')
      return calendarioService.marcarPlanPago(supabase!, id, datos, materializacion)
    },
    onSuccess: async (data, variables) => {
      queryClient.invalidateQueries({ queryKey: calendarioKeys.all })
      cambiarEstado('vencimiento', variables.id, 'presentado', 'plan_pago')

      // Notif: plan de pago registrado → responsables del cliente.
      // Migrado del patrón legacy de auto-notif al canónico (M1 cleanup post-review).
      if (!data?.cliente_id) return
      try {
        const clienteDisplay = await nombreCliente(data.cliente_id)
        const actor = user?.name || user?.email || 'Alguien'
        const destinatarios = await responsablesCliente(data.cliente_id)
        if (destinatarios.length === 0) return
        await notificar({
          tipo: 'vencimiento',
          titulo: 'Plan de pago registrado',
          mensaje: `${actor} registró un plan de pago para ${data.impuesto_nombre ?? 'un vencimiento'} (${data.periodo ?? '—'}) de ${clienteDisplay}`,
          enlace: `/calendario?vencimiento=${data.id}`,
          destinatarios,
        })
      } catch (err) {
        console.error('[notificaciones] error notificando plan de pago:', err)
      }
    },
  })
}

// ===========================================
// Hooks de Eventos
// ===========================================

/**
 * Hook para listar eventos
 */
export function useEventos(filtros?: FiltrosEventos) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: calendarioKeys.eventos(filtros),
    queryFn: () => calendarioService.listarEventos(supabase!, filtros),
      enabled: !!supabase && !supabaseLoading,
  })
}

/**
 * Hook para obtener eventos de un mes
 */
export function useEventosMes(year: number, month: number, workspace?: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: calendarioKeys.eventosMes(organizacionId, year, month, workspace),
    queryFn: () => calendarioService.obtenerEventosPorMes(supabase!, organizacionId!, year, month, workspace),
      enabled: !!supabase && !supabaseLoading && !!organizacionId,
  })
}

/**
 * Hook para obtener un evento por ID
 */
export function useEvento(id: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: calendarioKeys.evento(id),
    queryFn: () => calendarioService.obtenerEventoPorId(supabase!, id),
    enabled: !!supabase && !supabaseLoading && !!id,
  })
}

/**
 * ORR-538: el evento se borró mientras se sincronizaba. Se refresca la agenda para que deje de
 * mostrarlo y se avisa, sin anunciar éxito ni ofrecer un Meet para reintentar.
 */
const avisarEventoEliminado = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: calendarioKeys.all })
  toast.warning('El evento se eliminó mientras se guardaba', {
    description: 'Lo borraron desde otra pestaña o lo borró otro usuario.',
  })
}

/**
 * Hook para crear evento
 */
export function useCrearEvento() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { crear } = useAuditor()
  const { puede } = usePermissions()
  const { user, getAccessTokenSilently } = useAuth0()

  return useMutation({
    mutationFn: async (evento: EventoCalendarioInsert) => {
      assertPermiso(puede, 'crearEvento')
      // ORR-288 — sin esto created_by quedaba NULL siempre: nadie podía saber quién agendó.
      const creado = await calendarioService.crearEvento(supabase!, {
        ...evento,
        created_by: evento.created_by ?? user?.email ?? null,
      })

      // Solo las reuniones llevan videollamada (ORR-227 fase 3). De acá en más nada
      // puede tirar: el evento ya está guardado y vale con o sin link de Meet.
      if (creado.tipo !== 'reunion') return creado

      try {
        const token = await getAccessTokenSilently()
        return await calendarioService.sincronizarReunionConGoogle(supabase!, token, creado)
      } catch (error) {
        if (error instanceof EventoEliminadoError) throw error
        console.error('[calendario] No se pudo obtener el token para Google:', error)
        return creado
      }
    },
    onError: (error) => {
      if (!(error instanceof EventoEliminadoError)) return
      // El alta sí ocurrió: queda en la auditoría aunque otro la haya borrado enseguida.
      crear('evento' as never, error.evento.id, { titulo: error.evento.titulo, tipo: error.evento.tipo })
      avisarEventoEliminado(queryClient)
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: calendarioKeys.all })
      crear('evento' as never, data.id, { titulo: data.titulo, tipo: data.tipo })

      if (data.tipo === 'reunion' && !data.meet_url) {
        toast.warning('La reunión quedó agendada, pero no se pudo crear el link de Meet.', {
          description: 'Podés reintentarlo desde el detalle de la reunión.',
        })
      }
    },
  })
}

/**
 * Reintenta crear la videollamada de una reunión que quedó sin link (ORR-227 fase 3).
 *
 * Idempotente: si la reunión ya existe en Google, se actualiza en vez de crear otra.
 * Por eso apretar dos veces no deja dos reuniones en el calendario del estudio.
 */
export function useReintentarReunionGoogle() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { getAccessTokenSilently } = useAuth0()

  return useMutation({
    mutationFn: async (evento: EventoCalendario) => {
      const token = await getAccessTokenSilently()
      return calendarioService.sincronizarReunionConGoogle(supabase!, token, evento)
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: calendarioKeys.all })
      if (data.meet_url) {
        toast.success('Listo, la videollamada quedó creada.')
      } else {
        toast.error('Google sigue sin devolver el link.', {
          description: data.google_sync_error ?? undefined,
        })
      }
    },
    onError: (error) => {
      if (error instanceof EventoEliminadoError) return avisarEventoEliminado(queryClient)
      handleMutationError(error, 'Error al crear la videollamada')
    },
  })
}

/**
 * Consulta si el calendario del estudio ya tiene algo en esa ventana (ORR-301).
 *
 * Devuelve los eventos que se pisan para que el alta pueda mostrarlos y pedir confirmación.
 * NO bloquea: una superposición puede ser deliberada, y negarse a agendar sería peor que el
 * problema que esto viene a resolver.
 *
 * Sin `onError` con toast: quien llama decide qué mostrar, porque el fallo acá no interrumpe
 * el alta — sigue de largo y crea la reunión igual.
 *
 * Tampoco invalida queries, y react-doctor lo marca por eso: es un `useMutation` que NO muta.
 * La forma es deliberada — lo que se necesita es una llamada imperativa en medio del submit
 * (`mutateAsync`), no una query reactiva. Un `useQuery` con `enabled: false` + `refetch()`
 * haría lo mismo peor. Invalidar cache acá sería directamente incorrecto: no cambió nada.
 */
export function useVerificarDisponibilidad() {
  const { getAccessTokenSilently } = useAuth0()
  const { organizacionId } = useOrganizacion()

  return useMutation({
    mutationFn: async ({
      fechaInicio,
      fechaFin,
    }: {
      fechaInicio: string
      fechaFin: string
    }): Promise<EventoOcupado[]> => {
      // La function exige permiso de agendar en la organización (ORR-531).
      if (!organizacionId) throw new Error('No hay una organización activa')
      const token = await getAccessTokenSilently()
      return calendarioService.consultarDisponibilidad(token, organizacionId, fechaInicio, fechaFin)
    },
  })
}

/**
 * Lee en Google Calendar la respuesta de cada invitado de una reunión (ORR-544).
 *
 * Solo consulta una reunión con evento en Google y si el usuario tiene `verCalendario`.
 * `habilitado` dice si la consulta corre. Sin reintento automático: el detalle ofrece volver a leer.
 */
export function useInvitadosReunion(evento: EventoCalendario | null) {
  const { getAccessTokenSilently } = useAuth0()
  const { puede } = usePermissions()

  const eventoId = evento?.id ?? ''
  const habilitado =
    evento?.tipo === 'reunion' && !!evento.google_event_id && puede.verCalendario === true

  const query = useQuery({
    queryKey: calendarioKeys.invitadosReunion(eventoId),
    queryFn: async () => {
      const token = await getAccessTokenSilently()
      return calendarioService.leerInvitadosReunion(token, eventoId)
    },
    enabled: habilitado,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  return { query, habilitado }
}

/**
 * Hook para actualizar evento
 */
export function useActualizarEvento() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { editar } = useAuditor()
  const { puede } = usePermissions()
  const { getAccessTokenSilently } = useAuth0()

  return useMutation({
    mutationFn: async ({ id, datos }: { id: string; datos: EventoCalendarioUpdate }) => {
      assertPermiso(puede, 'editarEvento')
      const actualizado = await calendarioService.actualizarEvento(supabase!, id, datos)

      // Sin esto, cambiar la hora en BrainHub dejaba a Google con la vieja y el invitado
      // se presentaba al horario anterior. `sincronizarReunionConGoogle` ya hace PATCH
      // cuando hay `google_event_id`, así que alcanza con volver a llamarla.
      if (actualizado.tipo !== 'reunion' || !actualizado.google_event_id) return actualizado

      // Best-effort, igual que en el alta: el evento local ya cambió y es la fuente de
      // verdad. Un fallo queda en `google_sync_error` y la UI ofrece reintentar.
      try {
        const token = await getAccessTokenSilently()
        return await calendarioService.sincronizarReunionConGoogle(supabase!, token, actualizado)
      } catch (error) {
        if (error instanceof EventoEliminadoError) throw error
        console.error('[calendario] No se pudo obtener el token para Google:', error)
        return actualizado
      }
    },
    onError: (error) => {
      if (error instanceof EventoEliminadoError) avisarEventoEliminado(queryClient)
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: calendarioKeys.all })
      editar('evento' as never, variables.id, { cambios: variables.datos })

      if (data.tipo === 'reunion' && data.google_sync_error) {
        toast.warning('El cambio se guardó, pero Google quedó con los datos viejos.', {
          description: 'Podés reintentar la sincronización desde el detalle de la reunión.',
        })
      }
    },
  })
}

/**
 * Hook para eliminar evento
 */
export function useEliminarEvento() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { eliminar } = useAuditor()
  const { puede } = usePermissions()
  const { getAccessTokenSilently } = useAuth0()

  return useMutation({
    mutationFn: async (id: string) => {
      assertPermiso(puede, 'eliminarEvento')

      // Google PRIMERO, y si falla se aborta. Al revés, el evento y su Meet quedaban
      // vivos en la agenda del estudio sin el `google_event_id` para encontrarlos:
      // basura que nadie puede cancelar desde la app, con el invitado externo viendo
      // la reunión en pie. Ante fallo preferimos que la reunión siga existiendo en los
      // dos lados —consistente y reintentable— antes que borrarla solo de uno.
      const evento = await calendarioService.obtenerEventoPorId(supabase!, id)
      if (evento.google_event_id) {
        const token = await getAccessTokenSilently()
        await calendarioService.cancelarReunionEnGoogle(token, evento.google_event_id)
      }

      return calendarioService.eliminarEvento(supabase!, id)
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: calendarioKeys.all })
      eliminar('evento' as never, id, {})
    },
    onError: (error) => handleMutationError(error, 'Error al eliminar el evento'),
  })
}

// ===========================================
// Hooks de Calendario Unificado
// ===========================================

/**
 * Hook para obtener items del calendario (vencimientos + eventos)
 */
export function useItemsCalendario(year: number, month: number, filtros?: FiltrosCalendario) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: calendarioKeys.items(organizacionId, year, month, filtros),
    queryFn: () => calendarioService.obtenerItemsPorMes(supabase!, organizacionId!, year, month, filtros),
    enabled: !!supabase && !supabaseLoading && !!organizacionId,
  })
}

/**
 * Hook para obtener items del calendario agrupados por día
 */
export function useItemsCalendarioPorDia(year: number, month: number, filtros?: FiltrosCalendario) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: calendarioKeys.itemsPorDia(organizacionId, year, month, filtros),
    queryFn: () => calendarioService.obtenerItemsPorDia(supabase!, organizacionId!, year, month, filtros),
    enabled: !!supabase && !supabaseLoading && !!organizacionId,
  })
}

// ===========================================
// Hooks de Cumplimiento
// ===========================================

/**
 * Hook para obtener vencimientos para reporte de cumplimiento.
 * Fetches all vencimientos in a date range (default: last 12 months).
 */
export function useVencimientosCumplimiento(meses: number = 12) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()

  const { desde, hasta } = useMemo(() => {
    const ahora = new Date()
    const inicio = new Date(ahora.getFullYear(), ahora.getMonth() - meses, 1)
    return {
      desde: `${inicio.getFullYear()}-${String(inicio.getMonth() + 1).padStart(2, '0')}-01`,
      hasta: `${ahora.getFullYear()}-${String(ahora.getMonth() + 1).padStart(2, '0')}-${String(new Date(ahora.getFullYear(), ahora.getMonth() + 1, 0).getDate()).padStart(2, '0')}`,
    }
  }, [meses])

  return useQuery({
    queryKey: [...calendarioKeys.all, 'cumplimiento', organizacionId, meses],
    queryFn: () => calendarioService.listarVencimientos(supabase!, organizacionId!, { desde, hasta }),
    enabled: !!supabase && !supabaseLoading && !!organizacionId,
    staleTime: 60000,
  })
}

// ===========================================
// Hooks de Feriados
// ===========================================

/**
 * Hook para obtener feriados de un año
 */
export function useFeriados(anio: number) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: calendarioKeys.feriados(anio),
    queryFn: () => calendarioService.listarFeriados(supabase!, anio),
    enabled: !!supabase && !supabaseLoading,
    staleTime: Infinity, // Feriados no cambian durante la sesión
  })
}

/**
 * Hook para crear un feriado
 */
export function useCrearFeriado() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: (feriado: { fecha: string; nombre: string; tipo: string; jurisdiccion?: string | null }) => {
      assertPermiso(puede, 'editarVencimiento')
      return calendarioService.crearFeriado(supabase!, feriado)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calendarioKeys.all })
    },
  })
}

/**
 * Hook para eliminar un feriado
 */
export function useEliminarFeriado() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: (id: string) => {
      assertPermiso(puede, 'editarVencimiento')
      return calendarioService.eliminarFeriado(supabase!, id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calendarioKeys.all })
    },
  })
}

// ===========================================
// Hooks de Prórrogas Fiscales
// ===========================================

export const prorrogasKeys = {
  all: ['prorrogas'] as const,
  list: (filtros?: { impuesto_id?: string; periodo?: string }) =>
    [...prorrogasKeys.all, 'list', filtros] as const,
  vencimientosCalendario: (impuestoId: string | null) =>
    [...prorrogasKeys.all, 'vencimientos-calendario', impuestoId] as const,
}

/**
 * Hook para listar prórrogas activas
 */
export function useProrrogas(organizacionId?: string | null, filtros?: { impuesto_id?: string; periodo?: string }) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: prorrogasKeys.list(filtros),
    queryFn: () => calendarioService.listarProrrogas(supabase!, organizacionId!, filtros),
    enabled: !!supabase && !supabaseLoading && !!organizacionId,
  })
}

/**
 * Hook para listar los vencimientos del calendario AFIP de un impuesto (ORR-277).
 * Alimenta el selector de "qué vencimiento prorrogar".
 */
export function useVencimientosCalendario(impuestoId: string | null) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: prorrogasKeys.vencimientosCalendario(impuestoId),
    queryFn: () => calendarioService.listarVencimientosCalendario(supabase!, impuestoId!),
    enabled: !!supabase && !supabaseLoading && !!impuestoId,
  })
}

/**
 * Hook para crear prórroga
 */
export function useCrearProrroga() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: (prorroga: ProrrogaInsert) => {
      assertPermiso(puede, 'editarVencimiento')
      return calendarioService.crearProrroga(supabase!, prorroga)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: prorrogasKeys.all })
      queryClient.invalidateQueries({ queryKey: calendarioKeys.all })
      toast.success('Prórroga creada correctamente')
    },
    onError: (error) => {
      handleMutationError(error, 'Error al crear prórroga')
    },
  })
}

/**
 * Hook para eliminar prórroga
 */
export function useEliminarProrroga() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: (id: string) => {
      assertPermiso(puede, 'editarVencimiento')
      return calendarioService.eliminarProrroga(supabase!, id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: prorrogasKeys.all })
      queryClient.invalidateQueries({ queryKey: calendarioKeys.all })
      toast.success('Prórroga eliminada')
    },
    onError: (error) => {
      handleMutationError(error, 'Error al eliminar prórroga')
    },
  })
}

// ===========================================
// Obligaciones Recurrentes (Type B)
// ===========================================

/**
 * Hook para generar obligaciones recurrentes
 */
export function useGenerarRecurrentes() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: ({ periodo, organizacionId }: { periodo: string; organizacionId: string }) => {
      assertPermiso(puede, 'crearVencimiento')
      return calendarioService.generarObligacionesRecurrentes(supabase!, periodo, organizacionId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calendarioKeys.all })
    },
    onError: (error) => {
      console.error('Error generando obligaciones recurrentes:', error)
      toast.error('Error al generar obligaciones recurrentes')
    },
  })
}

/**
 * Hook para generar vencimientos de Balances (cierre + 4 meses)
 */
export function useGenerarBalances() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: ({ organizacionId, year }: { organizacionId: string; year: number }) => {
      assertPermiso(puede, 'crearVencimiento')
      return calendarioService.generarVencimientosBalances(supabase!, organizacionId, year)
    },
    onSuccess: (resultado) => {
      queryClient.invalidateQueries({ queryKey: calendarioKeys.all })
      if (resultado.clientes_sin_cierre > 0) {
        toast.warning(
          `${resultado.vencimientos_creados} vencimientos generados. ${resultado.clientes_sin_cierre} cliente(s) sin cierre de ejercicio.`
        )
      } else if (resultado.vencimientos_creados === 0) {
        toast.info('No se generaron vencimientos nuevos (ya existían)')
      } else {
        toast.success(`${resultado.vencimientos_creados} vencimientos de Balances generados`)
      }
    },
    onError: (error) => {
      console.error('Error generando balances:', error)
      toast.error('Error al generar vencimientos de Balances')
    },
  })
}

// ===========================================
// Re-exports
// ===========================================

export type {
  FiltrosVencimientos,
  FiltrosEventos,
  FiltrosCalendario,
  EventoCalendario,
  EventoCalendarioInsert,
  EventoCalendarioUpdate,
  ItemCalendario,
  ItemsDelDia,
  TipoEvento,
  EstadoEvento,
} from '../types/calendario'

export { TIPO_EVENTO_CONFIG, ESTADO_EVENTO_CONFIG, COLORES_CALENDARIO } from '../types/calendario'
